/**
 * Layout + styling constants for the hand-rolled sequence-diagram renderer.
 *
 * Colors are hardcoded hex/rgba values, mirroring the sibling Mermaid
 * renderers' convention (see `flowchart-constants.ts` / `timeline-constants.ts`)
 * rather than the theme `ColorType` palette (which only exposes accent/danger/
 * warn, not surface/border/text tokens). Sizes are estimated, not measured —
 * also matching the siblings.
 */

export const CHAR_WIDTH_RATIO = 0.6;
export const FONT_SIZE = 13;
export const MESSAGE_FONT_SIZE = 12;
export const TITLE_FONT_SIZE = 15;

/** Participant header box. */
export const HEADER_PADDING_X = 14;
export const HEADER_HEIGHT = 40;
export const HEADER_MIN_WIDTH = 80;
export const HEADER_RADIUS = 8;

/** Minimum center-to-center spacing between adjacent participant lifelines. */
export const MIN_LANE_GAP = 150;
/** Extra breathing room added to the half-widths when spacing lanes. */
export const LANE_GAP_PADDING = 40;

/** Vertical rhythm. */
export const TOP_PADDING = 16;
export const TITLE_GAP = 12;
export const FIRST_MESSAGE_GAP = 28;
export const MESSAGE_ROW_HEIGHT = 44;
export const NOTE_ROW_HEIGHT = 48;
export const BLOCK_LABEL_HEIGHT = 22;
export const BLOCK_PADDING_Y = 10;
export const BOTTOM_PADDING = 24;
export const LIFELINE_TAIL = 8;

/** Side padding so the outermost lanes / labels are not clipped. */
export const SIDE_PADDING = 24;

/** Note box. */
export const NOTE_PADDING_X = 12;
export const NOTE_MIN_WIDTH = 90;
export const NOTE_VERTICAL_INSET = 12;
export const NOTE_SIDE_OFFSET = 12;

/** Arrowhead size for message lines. */
export const ARROW_HEAD_SIZE = 8;
/** Self-message loop width. */
export const SELF_LOOP_WIDTH = 34;
export const SELF_LOOP_HALF_HEIGHT = 10;

/** Block frame padding around the events it encloses. */
export const BLOCK_FRAME_PADDING = 24;

// SVG attribute keyword values, hoisted to SCREAMING_CASE consts so they are
// not flagged as loose strings (the `local-rules/no-loose-string` rule exempts
// such const declarations). Dash patterns use space-separated values, which the
// rule treats as static whitespace-bearing text — matching flowchart's "6 4".
export const MARKER_ORIENT_AUTO = 'auto';
export const MARKER_UNITS_USERSPACE = 'userSpaceOnUse';
export const LIFELINE_DASH = '4 4';
export const BLOCK_FRAME_DASH = '3 3';

export function estimateTextWidth(text: string, fontSize: number = FONT_SIZE): number {
  return Math.ceil(text.length * fontSize * CHAR_WIDTH_RATIO);
}

// Palette — hardcoded hex/rgba to match the dark canvas chrome used by the
// other widgets (slate strokes, near-black fills, light text). Hoisted to
// SCREAMING_CASE consts so the raw color strings satisfy the repo's
// `local-rules/no-loose-string` rule, the same convention flowchart uses
// (`NODE_FILL`, `EDGE_LABEL_TEXT`, ...).
const SEQ_HEADER_FILL = '#131823';
const SEQ_HEADER_STROKE = 'rgba(255, 255, 255, 0.14)';
const SEQ_HEADER_TEXT = 'rgba(255, 255, 255, 0.88)';
const SEQ_LIFELINE = 'rgba(255, 255, 255, 0.12)';
const SEQ_MESSAGE_LINE = '#94A3B8';
const SEQ_MESSAGE_TEXT = 'rgba(255, 255, 255, 0.85)';
const SEQ_NOTE_FILL = '#0F131D';
const SEQ_NOTE_STROKE = 'rgba(255, 255, 255, 0.10)';
const SEQ_NOTE_TEXT = 'rgba(255, 255, 255, 0.82)';
const SEQ_BLOCK_STROKE = 'rgba(255, 255, 255, 0.12)';
const SEQ_BLOCK_LABEL_FILL = '#0F131D';
const SEQ_BLOCK_LABEL_TEXT = '#94A3B8';

/** Themed colors, resolved once. */
export const sequenceColors = {
  headerFill: SEQ_HEADER_FILL,
  headerStroke: SEQ_HEADER_STROKE,
  headerText: SEQ_HEADER_TEXT,
  lifeline: SEQ_LIFELINE,
  messageLine: SEQ_MESSAGE_LINE,
  messageText: SEQ_MESSAGE_TEXT,
  noteFill: SEQ_NOTE_FILL,
  noteStroke: SEQ_NOTE_STROKE,
  noteText: SEQ_NOTE_TEXT,
  blockStroke: SEQ_BLOCK_STROKE,
  blockLabelFill: SEQ_BLOCK_LABEL_FILL,
  blockLabelText: SEQ_BLOCK_LABEL_TEXT,
} as const;
